import { motion, AnimatePresence } from 'framer-motion'
import { X } from 'lucide-react'
import { useTranslation } from 'react-i18next'

interface TermsModalProps {
  isOpen: boolean
  onClose: () => void
}

export function TermsModal({ isOpen, onClose }: TermsModalProps) {
  const { t } = useTranslation()

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 z-[100]"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 pb-20 md:pb-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[calc(90vh-5rem)] md:max-h-[90vh] overflow-hidden flex flex-col">
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
                <h2 className="text-xl font-bold text-text-primary">
                  {t('auth.termsOfUse')}
                </h2>
                <button
                  onClick={onClose}
                  className="p-2 rounded-full hover:bg-gray-100 transition-colors"
                >
                  <X className="h-5 w-5 text-text-secondary" />
                </button>
              </div>

              <div className="overflow-y-auto px-6 py-4 flex-1">
                <div className="prose prose-sm max-w-none text-text-primary space-y-4">
                  <div>
                    <h3 className="text-lg font-semibold mb-2">
                      {t('terms.lastUpdated', 'Última atualização')}: {new Date().toLocaleDateString()}
                    </h3>
                    
                    <section className="mb-6">
                      <h4 className="font-semibold mb-2">{t('terms.acceptance', '1. Aceitação dos Termos')}</h4>
                      <p className="text-text-secondary text-sm leading-relaxed">
                        {t('terms.acceptanceText', 'Ao aceder e utilizar o Djelo Wallet, concorda em cumprir e estar vinculado a estes Termos de Uso. Se não concordar com estes termos, não deve utilizar o nosso serviço.')}
                      </p>
                    </section>

                    <section className="mb-6">
                      <h4 className="font-semibold mb-2">{t('terms.description', '2. Descrição do Serviço')}</h4>
                      <p className="text-text-secondary text-sm leading-relaxed">
                        {t('terms.descriptionText', 'O Djelo Wallet é uma plataforma de carteira digital que permite aos utilizadores fazer transações, gerir saldos e realizar pagamentos em São Tomé e Príncipe.')}
                      </p>
                    </section>

                    <section className="mb-6">
                      <h4 className="font-semibold mb-2">{t('terms.eligibility', '3. Elegibilidade')}</h4>
                      <p className="text-text-secondary text-sm leading-relaxed">
                        {t('terms.eligibilityText', 'Para utilizar o Djelo Wallet, deve ter pelo menos 18 anos de idade e ser residente ou estar em São Tomé e Príncipe. Ao registar-se, declara e garante que tem capacidade legal para celebrar este acordo.')}
                      </p>
                    </section>

                    <section className="mb-6">
                      <h4 className="font-semibold mb-2">{t('terms.account', '4. Conta de Utilizador')}</h4>
                      <p className="text-text-secondary text-sm leading-relaxed">
                        {t('terms.accountText', 'É responsável por manter a confidencialidade das suas credenciais de conta e por todas as atividades que ocorrem na sua conta. Deve notificar-nos imediatamente sobre qualquer uso não autorizado da sua conta.')}
                      </p>
                    </section>

                    <section className="mb-6">
                      <h4 className="font-semibold mb-2">{t('terms.transactions', '5. Transações')}</h4>
                      <p className="text-text-secondary text-sm leading-relaxed">
                        {t('terms.transactionsText', 'Todas as transações são processadas de forma segura. Reservamo-nos o direito de recusar ou cancelar qualquer transação suspeita. As transações podem estar sujeitas a limites e taxas conforme estabelecido nos nossos termos comerciais.')}
                      </p>
                    </section>

                    <section className="mb-6">
                      <h4 className="font-semibold mb-2">{t('terms.limitations', '6. Limitações de Responsabilidade')}</h4>
                      <p className="text-text-secondary text-sm leading-relaxed">
                        {t('terms.limitationsText', 'O Djelo Wallet é fornecido "como está" sem garantias de qualquer tipo. Não nos responsabilizamos por perdas ou danos resultantes da utilização do serviço, exceto quando expressamente previsto por lei.')}
                      </p>
                    </section>

                    <section className="mb-6">
                      <h4 className="font-semibold mb-2">{t('terms.modifications', '7. Modificações dos Termos')}</h4>
                      <p className="text-text-secondary text-sm leading-relaxed">
                        {t('terms.modificationsText', 'Reservamo-nos o direito de modificar estes termos a qualquer momento. As alterações entrarão em vigor imediatamente após a publicação. O uso continuado do serviço após as alterações constitui aceitação dos novos termos.')}
                      </p>
                    </section>
                  </div>
                </div>
              </div>

              <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4">
                <button
                  onClick={onClose}
                  className="w-full bg-primary text-white rounded-lg py-2 px-4 hover:bg-primary/90 transition-colors"
                >
                  {t('common.close')}
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}

